import PageHeading from '@shared/ui/PageHeading';
import { CreateCardBtn, HelpBtn } from '@widgets/buttons';
import CardsTable from '@widgets/tables/CardsTable';
import { useEffect, useCallback, useMemo } from 'react';
import {
  setCardsParams,
  setCardsSearch,
  resetCardsFilters,
  setSelected,
} from '@app/store/slices/cardsSlice';
import { useDispatch, useSelector } from 'react-redux';
import { SearchTable } from '@shared/ui/forms/search';
import TabsComponent from '@shared/ui/TabsComponent';
import { useCardsFilters } from '@utils/drawer-hooks';
import { FilterButton, ThemeButtonIcon } from '@shared/ui/buttons';
import { useTranslation } from 'react-i18next';
import { useMobileOverlay } from '@utils/hooks/useMobileOverlay';
import useMediaQuery from '@mui/material/useMediaQuery';
import { theme } from '@utils/libs/theme';
import { useCreateCardDialog } from '@utils/dialogs-hooks';
import TableSelectedButton from '@widgets/tables/main/TableSelectedButton';
import { roles } from '@shared/static/roles';

export default function CardsPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { openCreateCardDialog } = useCreateCardDialog();
  const { openCardsFilters } = useCardsFilters();

  const { search, filtersCount, selected, params } = useSelector(
    (state) => state.cards,
  );
  const { user } = useSelector((state) => state.user);
  const mobileSearch = useMobileOverlay();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const allTabsCardsOptions = [
    { label: t('Actual'), value: 'actual', icon: 'cards' },
    { label: t('Mine'), value: 'mine', icon: 'profile' },
    { label: t('Archive'), value: 'archive', icon: 'archive' },
  ];

  const tabsCardsOptions =
    user?.group_role === roles.TEAM_MEMBER
      ? allTabsCardsOptions.filter((option) => option.value !== 'mine')
      : allTabsCardsOptions;

  useEffect(() => {
    dispatch(setCardsParams({ page: 1, filter: 'actual' }));
  }, []);

  const handleSearchChange = useCallback(
    (nextSearch) => {
      dispatch(setCardsSearch(nextSearch));
    },
    [dispatch],
  );

  const cardsSearchElement = useMemo(() => {
    return (
      <SearchTable
        handleSearch={handleSearchChange}
        value={search}
        placeholder={t('cards_search_placeholder')}
      />
    );
  }, [search, handleSearchChange, t]);

  const filterButtonElement = useMemo(() => {
    return (
      <FilterButton
        onClick={openCardsFilters}
        filtersCount={filtersCount}
        resetFilters={resetCardsFilters}
      />
    );
  }, [filtersCount, openCardsFilters]);

  return (
    <section className="table-page cards-page main-section">
      {isMobile && mobileSearch.isOpen && (
        <div className="search-mobile-container">
          <div className="block-pd-x search-mobile">
            <ThemeButtonIcon icon="arrow-left" onClick={mobileSearch.close} />
            {cardsSearchElement}
          </div>
        </div>
      )}
      {isMobile && selected.length > 0 && (
        <div className="tableSelectedButton block-pd-x block-pd-y">
          <TableSelectedButton selected={selected} />
          <ThemeButtonIcon
            icon="close"
            onClick={() => dispatch(setSelected([]))}
          />
        </div>
      )}

      {(!isMobile || (!mobileSearch.isOpen && selected.length === 0)) && (
        <PageHeading title={t('Cards')}>
          <HelpBtn page="cards" />
          {isMobile && (
            <>
              {filterButtonElement}
              <ThemeButtonIcon icon="search" onClick={mobileSearch.open} />
            </>
          )}
          {!isMobile && <CreateCardBtn />}
          {isMobile && (
            <ThemeButtonIcon
              icon="add"
              appearance="primary"
              onClick={() => openCreateCardDialog()}
            />
          )}
        </PageHeading>
      )}

      <div className="block-pd-x table-page__box">
        <div className="table-filters">
          <div className="table-filters-row">
            {!isMobile && cardsSearchElement}
            <TabsComponent
              options={tabsCardsOptions}
              value={params.filter ?? 'actual'}
              setValue={(value) => {
                dispatch(setCardsParams({ page: 1, filter: value }));
                dispatch(setSelected([]));
              }}
              isGroup
              fullWidth={isMobile}
            />
          </div>
          {!isMobile && filterButtonElement}
        </div>
        <div className="table-page__table">
          <CardsTable />
        </div>
      </div>
    </section>
  );
}
